import type { RedirectMechanismProps } from '@apps/guest/routes/HotelBooking/HotelBooking.types';
import { RedirectMechanismType } from '@graphql/generated-base';

/**
 * Extracts the primary redirect mechanism (excluding 'partnerSearch') and the interceptor redirect (if any).
 *
 * @param redirectMechanisms - Array of redirect mechanism objects.
 * @returns An object containing the primary redirect and the interceptor redirect, if present.
 */
export function extractHotelRedirects(redirectMechanisms?: ReadonlyArray<RedirectMechanismProps> | null): {
  hotelPageUrl?: RedirectMechanismProps;
  hotelSearchPageUrl?: RedirectMechanismProps;
} {
  if (!redirectMechanisms) return { hotelPageUrl: undefined, hotelSearchPageUrl: undefined };
  return redirectMechanisms.reduce<{
    hotelPageUrl?: RedirectMechanismProps;
    hotelSearchPageUrl?: RedirectMechanismProps;
  }>((acc, redirect) => {
    if (redirect.type === RedirectMechanismType.partnerSearch) {
      acc.hotelSearchPageUrl ??= redirect;
    } else if (!acc.hotelPageUrl) {
      acc.hotelPageUrl = redirect;
    }
    return acc;
  }, {});
}

/**
 * Returns the telemetry destination name from the selected redirect mechanism.
 * Uses the same selection logic as useAccommodationRedirectUrl: prioritizes partnerSearch
 * over other mechanisms to ensure telemetry matches the actual redirect target.
 */
export function getSelectedRedirectTelemetryName(redirectMechanisms?: ReadonlyArray<RedirectMechanismProps> | null): string {
  const { hotelSearchPageUrl, hotelPageUrl } = extractHotelRedirects(redirectMechanisms);
  const selectedMechanism = hotelSearchPageUrl || hotelPageUrl;
  return selectedMechanism?.telemetryDestinationName || '(none)';
}
